import type { SafeHtmlAttributes } from "../../types/SafeHtmlAttributes";

import styles from "./SkeletonText.module.scss";

const LONGEST_ROW_SCALE = 0.95;
const SHORT_ROW_SCALE = 0.9;
const LAST_ROW_SCALE = 0.5;

const getNormalizedRows = (rows: number): number => {
  if (!Number.isFinite(rows)) {
    return 1;
  }

  return Math.max(1, Math.floor(rows));
};

const getRowScale = (index: number, rows: number, fullWidth: boolean): number => {
  if (fullWidth) {
    return LONGEST_ROW_SCALE;
  }

  if (index === rows - 1) {
    return LAST_ROW_SCALE;
  }

  return index % 2 === 0 ? LONGEST_ROW_SCALE : SHORT_ROW_SCALE;
};

export type SkeletonTextProps = SafeHtmlAttributes<HTMLDivElement> & {
  rows?: number;
  fullWidth?: boolean;
};

export const SkeletonText = ({
  rows = 2,
  fullWidth = false,
  "aria-hidden": ariaHidden = true,
  ...rest
}: SkeletonTextProps) => {
  const normalizedRows = getNormalizedRows(rows);
  const rowScales = Array.from({ length: normalizedRows }, (_value, index) =>
    getRowScale(index, normalizedRows, fullWidth)
  );

  return (
    <div {...rest} aria-hidden={ariaHidden} className={styles.skeletonText}>
      <div aria-hidden="true" className={styles.rows}>
        {rowScales.map((rowScale, index) => {
          const rowStyle = { width: `${rowScale * 100}%`, "--row-scale": rowScale.toString() };

          return (
            <div key={index} className={styles.row} data-testid="skeleton-text-row" style={rowStyle}>
              <div className={styles.rowHighlight} />
            </div>
          );
        })}
      </div>
    </div>
  );
};
